import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CONTEXTUAL_MENU_ACTIVE_ESCAPE_BOUNDARY_ATTR,
  CONTEXTUAL_MENU_ESCAPE_BOUNDARY_ATTR
} from '../menu-constants.ts';

import type { MenuNav, MenuPage } from './useMenuNav.ts';

type UseDrilldownStackOptions = {
  onClose: (event?: Event | React.MouseEvent | React.KeyboardEvent) => void;
  closeAllSubmenus: () => void;
  activeSubmenuKey: string | null;
  /** When provided, menu opens directly on this page, with no back navigation. */
  initialPage?: MenuPage;
};

type UseDrilldownStackResult = {
  /** The `MenuNav` object — provides push/pop/replace/close/depth to descendants. */
  nav: MenuNav;
  /** The currently visible page, or null when at the root items level. */
  currentPage: MenuPage | null;
  /** Stack depth: 0 = root items, 1+ = drilldown page. */
  depth: number;
  /** True when the current page uses a `content` render-prop (not structured menu items). */
  isContentPage: boolean;
  /** Keyboard handler for depth-aware Escape. Attach to a wrapper element. */
  handleKeyDown: (e: React.KeyboardEvent) => void;
};

const isInsideEscapeBoundary = (target: EventTarget | null) =>
  target instanceof Element &&
  target.closest(`[${CONTEXTUAL_MENU_ESCAPE_BOUNDARY_ATTR}="true"]`) !== null;

const hasActiveEscapeBoundary = () =>
  document.querySelector(`[${CONTEXTUAL_MENU_ACTIVE_ESCAPE_BOUNDARY_ATTR}="true"]`) !==
  null;

/**
 * Manages the menu-stack state for in-place drilldown navigation.
 *
 * Keyboard nav behavior:
 * - Pressing Escape at depth > 0 pops one level (unless a submenu is open)
 * - Pressing Escape at depth 0 closes the entire popup
 */
export const useDrilldownStack = ({
  onClose,
  closeAllSubmenus,
  activeSubmenuKey,
  initialPage
}: UseDrilldownStackOptions): UseDrilldownStackResult => {
  const [menuStack, setMenuStack] = useState<MenuPage[]>(
    initialPage ? [initialPage] : []
  );
  const hasInitialPage = initialPage != null;

  // Keep the stack in sync when the initialPage changes (e.g. when data it depends on updates).
  useEffect(() => {
    if (initialPage) {
      setMenuStack((prev) => {
        // Only update the bottom entry (the initial page) if the stack hasn't been
        // navigated deeper, to avoid clobbering drilldown pages pushed by the user.
        if (prev.length <= 1) {
          return [initialPage];
        }
        return [initialPage, ...prev.slice(1)];
      });
    }
  }, [initialPage]);

  const currentPage = menuStack.length > 0 ? menuStack[menuStack.length - 1] : null;
  const depth = menuStack.length;
  const isContentPage = currentPage?.content != null;
  const pushPage = useCallback(
    (page: MenuPage) => {
      closeAllSubmenus();
      setMenuStack((prev) => [...prev, page]);
    },
    [closeAllSubmenus]
  );

  const popPage = useCallback(() => {
    setMenuStack((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      // When an initialPage is set, it's the only entry in the stack,
      // so there's nothing to pop back to — close the menu instead.
      if (hasInitialPage && prev.length <= 1) {
        onClose?.();
        return prev;
      }
      closeAllSubmenus();
      return prev.slice(0, -1);
    });
  }, [closeAllSubmenus, hasInitialPage, onClose]);

  const replacePage = useCallback(
    (page: MenuPage) => {
      closeAllSubmenus();
      setMenuStack((prev) => (prev.length === 0 ? [page] : [...prev.slice(0, -1), page]));
    },
    [closeAllSubmenus]
  );

  const nav = useMemo<MenuNav>(
    () => ({
      close: () => onClose?.(),
      pop: popPage,
      push: pushPage,
      replace: replacePage,
      depth
    }),
    [onClose, popPage, pushPage, replacePage, depth]
  );

  // Ensure Escape pops the drilldown stack even when focus is on an element outside
  // the DrilldownWrapper (e.g. after clicking empty space in the popup). Without this,
  // the popup's own handler fires first and closes the entire menu instead of navigating back.
  useEffect(() => {
    if (depth <= 0 || activeSubmenuKey !== null) {
      return;
    }

    const handleEscapeCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      if (isInsideEscapeBoundary(e.target)) {
        return;
      }
      if (hasActiveEscapeBoundary()) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      popPage();
    };

    document.addEventListener('keydown', handleEscapeCapture, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeCapture, true);
    };
  }, [activeSubmenuKey, depth, popPage]);

  // Escape handler pops the stack if depth > 0, otherwise closes the popup.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      if (activeSubmenuKey !== null) {
        return;
      }
      if (isInsideEscapeBoundary(e.target)) {
        return;
      }
      if (hasActiveEscapeBoundary()) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation?.();
      if (depth > 0) {
        popPage();
      } else {
        onClose?.();
      }
    },
    [activeSubmenuKey, depth, popPage, onClose]
  );

  return {
    nav,
    currentPage,
    depth,
    isContentPage,
    handleKeyDown
  };
};
